<template>
  <div class="project-page">
    <SiteNavigationBar></SiteNavigationBar>
    <Transition
      enter-active-class="transition duration-[0.21] ease-in-out"
      enter-from-class="transform translate-y-[200%]"
      enter-to-class="transform translate-y-0"
      leave-active-class="transition duration-[0.1] ease-in-out"
      leave-from-class="transform translate-y-0"
      leave-to-class="transform translate-y-[200%]"
    >
      <div
        v-if="
          result &&
          !buttonsAreVisible &&
          !contactFormIsVisible &&
          !footerIsVisible
        "
        class="flex items-center h-[48px] !fixed bottom-[20px] sm:bottom-[32px] z-[58] bg-primary rounded absolute-center-x -translate-x-1/2 shadow"
      >
        <AppButton
          class="text-white variant-invisible uppercase h-full max-md:text-sm min-[390px]:whitespace-nowrap px-3 py-2 rounded"
          to="#contact-form"
        >
          {{ $t('property.contactAgent') }}
        </AppButton>
      </div>
    </Transition>
    <VueEasyLightbox
      :visible="visibleRef"
      :imgs="imgsRef"
      :index="indexRef"
      :scroll-disabled="false"
      rotate-disabled
      move-disabled
      @hide="onHide"
      @on-prev="updateIndex"
      @on-next="updateIndex"
    >
      <template #prev-btn="{ prev }">
        <button
          class="btn-prev absolute top-1/2 left-2 transform -translate-y-1/2 bg-white rounded-full p-2 z-50 opacity-60 focus:outline-none"
          :disabled="indexRef === 0"
          @click="prev"
        >
          <Icon class="text-neutral-light w-8 h-8" :path="mdiChevronLeft" />
        </button>
      </template>

      <template #next-btn="{ next }">
        <button
          class="btn-next absolute top-1/2 right-2 transform -translate-y-1/2 bg-white rounded-full p-2 z-50 opacity-60 focus:outline-none"
          :disabled="indexRef === imgsRef?.length - 1"
          @click="next"
        >
          <Icon class="text-neutral-light w-8 h-8" :path="mdiChevronRight" />
        </button>
      </template>
    </VueEasyLightbox>
    <main
      class="w-full !min-h-[100lvh] min-h-[100vh] px-4 pt-[calc(60px+1rem)] md:pt-[calc(88px+1rem)] pb-8"
    >
      <div
        id="search-bar-container"
        class="h-[64px] pt-2 flex justify-center w-[calc(100%-0.075rem)] -ml-4 -mt-4 bg-white fixed z-[51]"
      >
        <SiteSearchBar
          class="variant-outline-lg !w-[calc(100vw-32px)] max-w-[1280px] mx-auto h-[48px] fix-h"
          field-name="search-bar"
          :placeholder="$t('search.searchPlaceholder')"
          use-location-from-store
          @update:model-value="searchLocation = $event"
        >
          <template #button>
            <Icon
              class="text-neutral-light w-8 h-full"
              :path="mdiMapMarkerOutline"
            />
          </template>
        </SiteSearchBar>
      </div>
      <section
        id="project-info-section"
        class="max-w-[1280px] mx-auto mb-6 pt-[44px] md:pt-[36px]"
      >
        <div
          class="relative flex gap-x-4 pb-1 md:pb-4 md:pt-4 items-center w-[calc(100%-1rem)] -ml-2 max-md:flex-wrap"
        >
          <AppButton
            class="variant-invisible pl-1 pr-2 h-[40px] rounded"
            :to="searchLink"
            @click="backToSearchEvent"
          >
            <Icon :path="mdiArrowLeft" />
            {{ $t('property.backToSearch') }}
          </AppButton>
          <SiteBreadcrumbs
            class="max-md:hidden flex-1 min-[432px]:min-w-[400px] max-w-[calc(100%-24px)] mx-auto !py-0"
            :items="breadcrumbs"
          />
        </div>
        <div
          v-if="summary"
          id="project-summary"
          class="text-neutral-light text-sm mb-4"
        >
          {{ summary }}
        </div>
        <div
          class="w-full max-w-[100vw] sm:min-w-[320px] lg:min-w-[440px] max-[470px]:mx-[-1rem] max-[470px]:w-[calc(100%+2rem)] max-h-[505px]"
        >
          <Splide
            v-if="images && images.length > 1"
            ref="splide"
            :options="{
              perPage: 1,
              perMove: 1,
              pagination: false,
              lazyLoad: 'nearby',
              loop: true,
              role: 'group'
            }"
            class="max-md:aspect-[4/3] max-h-[505px] min-[470px]:rounded-lg overflow-hidden"
          >
            <SplideSlide
              v-for="({ image, imageSet, imageAlt }, index) in images"
              :key="`${index}-slide`"
            >
              <img
                v-if="image"
                :data-splide-lazy="image"
                :data-splide-lazy-srcset="imageSet"
                :alt="imageAlt"
                class="w-full max-md:max-w-full max-md:aspect-[4/3] max-h-[505px] object-cover object-center cursor-pointer"
                @click="() => show(index)"
              />
            </SplideSlide>
          </Splide>
          <img
            v-else-if="images && images.length && images[0].image"
            class="max-md:max-w-[480px] max-md:aspect-[4/3] max-h-[505px] h-full w-full object-cover object-center"
            :class="{
              'bg-white !object-contain': images[0].placeholder,
              'cursor-pointer': !images[0].placeholder
            }"
            :src="images[0].image"
            :srcset="images[0].imageSet"
            :alt="images[0].imageAlt"
            @click="() => (images[0].placeholder ? null : show(0))"
          />
        </div>
        <div
          class="flex flex-wrap justify-center items-start gap-12 max-md:max-w-[480px] mx-auto"
        >
          <div
            v-if="result?.projectName"
            id="more-details"
            class="min-w-[250px] mr-auto flex-1"
          >
            <div v-if="isBuilding" class="min-h-[78px] mt-2">
              <div
                class="max-md:absolute max-md:left-0 w-full min-h-[78px] bg-white"
              >
                <Tabs
                  v-model:selected="selectedTab"
                  class="-mb-[0.75px] highlight-active text-sm lg:text-base"
                  variant="projects"
                  :tabs="tabOptions"
                />
              </div>
            </div>
            <template v-if="selectedTab === 0">
              <div
                v-if="result?.projectName"
                id="project-info"
                class="flex-1 max-lg:leading-7 leading-8 gap-1 max-lg:text-base text-lg"
              >
                <div class="w-full relative">
                  <span
                    class="text-lg md:text-xl lg:text-2xl leading-[1.2em] mt-2 mb-1 max-w-[calc(100%-50px)] flex items-center gap-2"
                  >
                    <h1 class="[text-wrap:balance]">
                      {{ title }}
                    </h1>
                    <AppButton
                      class="variant-invisible icon-button h-[36px] w-[36px] min-w-[36px] rounded-full"
                      :icon="mdiShareVariant"
                      :title="$t('property.share')"
                      :aria-label="$t('property.share')"
                      @click="shareDialogIsVisible = true"
                    />
                    <ShareDialog
                      :class="{
                        'opacity-0 pointer-events-none': !shareDialogIsVisible
                      }"
                      class="transition duration-300 ease-in-out"
                      :url="link"
                      :text="t('project.shareText')"
                      :title="result?.projectName || t('project.shareTitle')"
                      @close="shareDialogIsVisible = false"
                    />
                  </span>
                  <div
                    v-if="result.minPrice || result.maxPrice"
                    class="text-xl font-bold"
                  >
                    <template v-if="result.minPrice !== result.maxPrice">
                      {{
                        $t('common.range', [
                          $n(result.minPrice, 'currency', language),
                          $n(result.maxPrice, 'currency', language)
                        ])
                      }}
                    </template>
                    <template v-else>
                      {{ $n(result.minPrice, 'currency', language) }}
                    </template>
                  </div>
                  <h2 class="text-lg mb-2">
                    {{ completeAddress }}
                  </h2>
                  <div
                    class="grid grid-cols-2 min-[968px]:grid-cols-3 lg:grid-cols-4 gap-3 max-w-[768px] w-full max-lg:text-sm text-base mb-5"
                  >
                    <SiteDetailsProperty
                      v-if="result.yearCompletion"
                      class="w-full min-w-[160px] whitespace-nowrap"
                      :label="$t('project.yearCompletionShort')"
                      :value="result.yearCompletion"
                      :icon="mdiCalendarMonth"
                    >
                    </SiteDetailsProperty>
                    <SiteDetailsProperty
                      v-if="result.stories"
                      class="w-full min-w-[160px] whitespace-nowrap"
                      :label="$t('project.stories')"
                      :value="result.stories"
                      :icon="mdiFloorPlan"
                    >
                    </SiteDetailsProperty>
                    <SiteDetailsProperty
                      v-if="result.totalUnits"
                      class="w-full min-w-[160px] whitespace-nowrap"
                      :label="$t('project.residences')"
                      :value="result.totalUnits"
                      :icon="mdiHomeCity"
                    >
                    </SiteDetailsProperty>
                    <SiteDetailsProperty
                      v-if="result.minBeds || result.maxBeds"
                      class="w-full min-w-[160px] whitespace-nowrap"
                      :label="$t('property.bedrooms')"
                      :value="
                        result.minBeds !== result.maxBeds
                          ? $t('common.range', [result.minBeds, result.maxBeds])
                          : result.minBeds
                      "
                      :icon="mdiBed"
                    >
                    </SiteDetailsProperty>
                    <SiteDetailsProperty
                      v-if="
                        (result.minBaths || result.maxBaths) &&
                        result.minBaths !== 999 &&
                        result.maxBaths !== 999
                      "
                      class="w-full min-w-[160px] whitespace-nowrap"
                      :label="$t('property.bathrooms')"
                      :value="
                        result.minBaths !== result.maxBaths
                          ? $t('common.range', [
                              result.minBaths,
                              result.maxBaths
                            ])
                          : result.minBaths
                      "
                      :icon="mdiBathtub"
                    >
                    </SiteDetailsProperty>
                    <SiteDetailsProperty
                      v-if="
                        (result.minSqft || result.maxSqft) &&
                        result.minSqft !== 999 &&
                        result.maxSqft !== 999
                      "
                      class="w-full min-w-[160px] whitespace-nowrap"
                      :label="$t('project.areaSqft')"
                      :value="
                        result.minSqft !== result.maxSqft
                          ? $t('common.range', [result.minSqft, result.maxSqft])
                          : result.minSqft
                      "
                      :icon="mdiRulerSquare"
                    >
                    </SiteDetailsProperty>
                  </div>
                  <div
                    v-if="result.floorPlans || result.brochures?.[0]"
                    id="contact-buttons"
                    v-element-visibility="(v: boolean) => buttonsAreVisible = v"
                    class="flex flex-wrap gap-x-2 lg:gap-x-4 gap-y-2 text-base mb-4"
                  >
                    <AppButton
                      v-if="result.floorPlans"
                      class="max-lg:text-sm px-2 max-lg:h-[40px] h-[48px] rounded shadow"
                      target="_blank"
                      :to="fixFileUrl(result.floorPlans)"
                    >
                      <Icon :path="mdiDownload" class="w-6" />
                      {{ $t('project.floorPlans') }}
                    </AppButton>
                    <AppButton
                      v-if="result.brochures?.[0]"
                      class="max-lg:text-sm px-2 max-lg:h-[40px] h-[48px] rounded shadow"
                      target="_blank"
                      :to="fixFileUrl(result.brochures[0])"
                    >
                      Brochure
                    </AppButton>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="flex flex-wrap justify-center items-stretch gap-6 max-md:max-w-[480px] max-md:mx-auto"
              >
                <div class="max-lg:max-w-[480px] flex flex-col gap-4 pt-2">
                  <div
                    class="w-[160px] h-[28px] rounded bg-gray-lighter animate-pulse"
                  ></div>
                  <div
                    class="w-[220px] h-[20px] rounded bg-gray-lighter animate-pulse"
                  ></div>
                  <div
                    class="w-[240px] h-[20px] rounded bg-gray-lighter animate-pulse"
                  ></div>
                  <div
                    class="w-[180px] h-[20px] rounded bg-gray-lighter animate-pulse"
                  ></div>
                  <div
                    class="w-[200px] h-[20px] rounded bg-gray-lighter animate-pulse"
                  ></div>
                  <div class="flex gap-3 h-[88px] mt-5 mb-3">
                    <div
                      class="w-[78px] h-full rounded bg-gray-lighter animate-pulse"
                    ></div>
                    <div
                      class="w-[78px] h-full rounded bg-gray-lighter animate-pulse"
                    ></div>
                    <div
                      class="w-[92px] h-full rounded bg-gray-lighter animate-pulse"
                    ></div>
                    <div
                      class="w-[140px] h-full rounded bg-gray-lighter animate-pulse"
                    ></div>
                  </div>
                  <div
                    class="w-[200px] h-[20px] rounded bg-gray-lighter animate-pulse"
                  ></div>
                  <div class="flex gap-3 h-[48px] mt-1 mb-4">
                    <div
                      class="w-[150px] h-full rounded bg-gray-lighter animate-pulse"
                    ></div>
                    <div
                      class="w-[150px] h-full rounded bg-gray-lighter animate-pulse"
                    ></div>
                  </div>
                </div>
              </div>
              <div
                v-if="result.description?.length > 3 || marker?.position"
                class="mb-6 max-w-[768px]"
              >
                <h2 class="uppercase text-lg md:text-xl mb-4">
                  {{ $t('project.projectDetails') }}
                </h2>
                <div
                  v-if="result.description?.length > 3"
                  class="html-content"
                  v-html="result.description"
                ></div>
              </div>
              <div
                v-else-if="!result"
                class="w-full flex flex-col gap-4 mb-6 max-w-[768px]"
              >
                <div
                  class="w-[160px] h-[28px] rounded bg-gray-lighter animate-pulse"
                ></div>
                <div
                  class="w-full h-[20px] rounded bg-gray-lighter animate-pulse"
                ></div>
                <div
                  class="w-full h-[20px] rounded bg-gray-lighter animate-pulse"
                ></div>
                <div
                  class="w-full h-[20px] rounded bg-gray-lighter animate-pulse"
                ></div>
                <div
                  class="w-1/2 h-[20px] rounded bg-gray-lighter animate-pulse"
                ></div>
              </div>
              <AppPopover
                v-if="!isInMobile && marker?.position"
                class="left-0 w-max p-4 flex flex-col items-start overflow-visible"
                container-class="inline-block mb-2"
                name="location"
              >
                <AppButton
                  class="variant-search search-controls-button"
                  :class="{
                    'active-filter': Object.values(placesLayers).some(Boolean)
                  }"
                >
                  <span class="mr-auto">{{ t('search.showPlaces') }}</span>
                  <Icon :path="mdiChevronDown" />
                </AppButton>
                <template #panel>
                  <div
                    class="flex flex-col gap-1 max-h-[calc(100vh-275px)] overflow-auto"
                  >
                    <AppSwitch
                      v-for="layer in placesOptions"
                      :key="`layer-toggle-${layer.key}`"
                      v-model="placesLayers[layer.key]"
                      class="!text-neutral-light"
                      :name="`layer-toggle-${layer.key}`"
                      :title="$t(layer.title)"
                      :label="$t(layer.title)"
                    />
                  </div>
                </template>
              </AppPopover>
              <div
                v-if="marker?.position || cardProps"
                id="map-container"
                class="relative w-full mb-6 max-w-[768px]"
                :class="[isInMobile ? '!h-[250px]' : '!h-[400px]']"
              >
                <ClientOnly>
                  <GMapMap
                    v-if="isInMobile"
                    class="w-full h-full border border-line rounded-lg overflow-hidden"
                    :api-key="gMapsKey"
                    :center="
                      marker?.position || { lat: 25.761681, lng: -80.191788 }
                    "
                    :zoom="marker?.position ? 12 : 7"
                    map-type-id="terrain"
                  >
                    <GMapMarker v-if="marker" :options="marker" />
                  </GMapMap>
                  <SiteSearchMap3D
                    v-else-if="cardProps"
                    class="w-full h-full border border-line rounded-lg overflow-hidden"
                    :center="
                      marker?.position || { lat: 25.761681, lng: -80.191788 }
                    "
                    :places-layers="placesLayers"
                    :favorites="[]"
                    :markers="[cardProps]"
                  />
                </ClientOnly>
              </div>
              <details
                v-for="section in sections.filter(
                  s => s in result && result[s]?.length > 3
                )"
                :key="section"
                class="border-b border-line mb-6 max-w-[768px]"
              >
                <summary class="flex items-center gap-2 min-h-[40px]">
                  <h2 class="uppercase text-lg">
                    {{ $t(`project.${section}`) }}
                  </h2>
                  <Icon
                    class="arrow text-neutral-light w-6 h-6 ml-auto"
                    :path="mdiChevronDown"
                  />
                </summary>
                <div
                  class="html-content content mt-4"
                  v-html="result[section]"
                ></div>
              </details>
            </template>
            <template v-else-if="selectedTab >= 1 && selectedTab <= 3">
              <!-- Filters -->
              <SiteProjectTable
                :key="`table-${selectedTab}`"
                :project-name="result.projectName"
                :project-filter="
                  getBuildingFilters(
                    selectedTab === 1
                      ? 'active'
                      : selectedTab === 2
                      ? 'pending'
                      : selectedTab === 3
                      ? 'closed'
                      : null
                  )
                "
              />
            </template>
            <KeepSection :show="selectedTab == 4" class="my-4 max-w-full">
              <AppSelector
                v-model="transactionType"
                class="variant-search mr-auto mb-4 inline-block"
                :options="chartTransactionTypeOptions"
                name="buy-type"
              />
              <template v-if="transactionType === 'RES'">
                <AnalyticsSalesTable
                  :building-name="result?.projectName"
                  :total-units="result?.totalUnits"
                  class="mb-6"
                />
                <AnalyticsSalesChart
                  :building-name="result?.projectName"
                  :total-units="result?.totalUnits"
                  class="w-full"
                />
              </template>
              <template v-else>
                <AnalyticsRentalTable
                  :building-name="result?.projectName"
                  :total-units="result?.totalUnits"
                  class="mb-6"
                />
                <AnalyticsSalesChart
                  :building-name="result?.projectName"
                  :total-units="result?.totalUnits"
                  class="w-full"
                  rentals
                />
              </template>
            </KeepSection>
          </div>
          <div
            v-if="selectedTab === 0"
            class="w-full self-start max-w-[405px] mt-4 md:sticky md:top-[158px]"
          >
            <div
              v-if="result?.projectStatus"
              v-element-visibility="(v: boolean) => contactFormIsVisible = v"
              class="border border-line-light rounded-lg shadow-xl-right px-3 py-4 text-center"
            >
              <div id="contact-form" class="-mt-[116px] pt-[116px]"></div>
              <h5 class="text-lg font-bold mb-2">
                {{ $t('contactForm.title') }}
              </h5>
              <p class="text-[14px] mb-2 [text-wrap:balance]">
                {{ $t('contactForm.description') }}
              </p>
              <HubspotForm
                element-id="project-form"
                :form-id="
                  isBuilding
                    ? {
                        en: 'b39fa0b0-1c2a-4c2d-94e4-e352f3f0ef5e',
                        es: '8f3d0898-565b-46fd-996e-f32d53676de0'
                      }
                    : {
                        en: 'cfdd2ded-6a7e-45d2-bfef-c77844c57a28',
                        es: 'dec32344-f669-47c3-9fb6-e494d25a824f'
                      }
                "
              />
            </div>
          </div>
        </div>
      </section>
      <section
        v-if="similarProjects.length || !similarProjectsResponse"
        id="similar-projects"
        class="max-w-[1280px] max-md:max-w-[480px] mx-auto"
      >
        <h3 class="uppercase text-lg md:text-xl mb-4">
          {{ $t('project.similarProjects') }}
        </h3>
        <div
          class="max-sm:grid max-sm:grid-cols-1 max-sm:w-auto max-sm:mx-auto sm:flex flex-wrap gap-4 max-w-[928px]"
        >
          <template v-if="similarProjects.length">
            <SiteProjectCard
              v-for="item in similarProjects"
              :key="`${item.id}-card`"
              class="max-sm:w-full max-sm:aspect-auto"
              v-bind="item"
            ></SiteProjectCard>
          </template>
          <template v-else-if="!similarProjectsResponse">
            <SitePropertyCardPlaceholder
              v-for="(item, index) in Array.from({
                length: SIMILAR_PROPERTIES
              })"
              :key="`${index}-placeholder`"
            >
            </SitePropertyCardPlaceholder>
          </template>
        </div>
      </section>
      <SiteBreadcrumbs
        class="md:hidden flex-1 min-[432px]:min-w-[400px] max-w-[calc(100%-24px)] mx-auto !py-0"
        :items="breadcrumbs"
      />
    </main>
    <SiteFooter
      v-once
      v-element-visibility="(v: boolean) => footerIsVisible = v"
      :fix-locale="locale"
    />
  </div>
</template>

<script setup lang="ts">
import {
  mdiArrowLeft,
  mdiBathtub,
  mdiBed,
  mdiCalendarMonth,
  mdiChartBoxOutline,
  mdiChevronDown,
  mdiChevronLeft,
  mdiChevronRight,
  mdiDownload,
  mdiFileSign,
  mdiFloorPlan,
  mdiHomeCity,
  mdiMagnify,
  mdiMapMarkerOutline,
  mdiOfficeBuilding,
  mdiRulerSquare,
  mdiShareVariant,
  mdiTimerSand
} from '@mdi/js';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css/core';
import { vElementVisibility } from '@vueuse/components';
import { useShare } from '@vueuse/core';
import VueEasyLightbox, { useEasyLightbox } from 'vue-easy-lightbox';
import { GoogleMap as GMapMap, Marker as GMapMarker } from 'vue3-google-map';

import { fixFileUrl, fixImageUrl } from '@/helpers/fixes';
import {
  placesOptions,
  propertySubTypeOptions,
  propertyTypeMap
} from '@/helpers/search';
import { animateDetailsElements } from '@/helpers/utils';
import { useSearchStore } from '@/store/search';
import type { QueryValue } from '@/types/common';
import type {
  Layer,
  ProjectCardProps,
  ProjectCardPropsExtended,
  ProjectDocumentResponse,
  QueryResponse
} from '@/types/properties';

const AppSelector = defineAsyncComponent(
  () => import('@/async-components/App/Selector.vue')
);
const AnalyticsRentalTable = defineAsyncComponent(
  () => import('@/async-components/Analytics/RentalTable.vue')
);
const AnalyticsSalesTable = defineAsyncComponent(
  () => import('@/async-components/Analytics/SalesTable.vue')
);
const AnalyticsSalesChart = defineAsyncComponent(
  () => import('@/async-components/Analytics/SalesChart.vue')
);
const AppPopover = defineAsyncComponent(
  () => import('@/async-components/App/Popover.vue')
);
const AppSwitch = defineAsyncComponent(
  () => import('@/async-components/App/Switch.vue')
);
const HubspotForm = defineAsyncComponent(
  () => import('@/async-components/HubspotForm.vue')
);
const SiteBreadcrumbs = defineAsyncComponent(
  () => import('@/async-components/Site/Breadcrumbs.vue')
);
const SiteNavigationBar = defineAsyncComponent(
  () => import('@/async-components/Site/NavigationBar.vue')
);
const SiteDetailsProperty = defineAsyncComponent(
  () => import('@/async-components/Site/Details/Property.vue')
);
const SiteProjectTable = defineAsyncComponent(
  () => import('@/async-components/Site/Project/Table.vue')
);
const SiteProjectCard = defineAsyncComponent(
  () => import('@/async-components/Site/ProjectCard.vue')
);
const SitePropertyCardPlaceholder = defineAsyncComponent(
  () => import('@/async-components/Site/PropertyCardPlaceholder.vue')
);
const Tabs = defineAsyncComponent(
  () => import('@/async-components/Tabs/Tabs.vue')
);
const SiteSearchBar = defineAsyncComponent(
  () => import('@/async-components/Site/Search/Bar.vue')
);
const SiteSearchMap3D = defineAsyncComponent(
  () => import('@/async-components/Site/Search/Map3D.vue')
);
const ShareDialog = defineAsyncComponent(
  () => import('@/async-components/App/ShareDialog.vue')
);

const sections = [
  'features',
  'amenities',
  'services',
  'exteriorAspects',
  'propertyDescriptions',
  'location',
  'layout',
  'salesPolicies',
  'financing',
  'deliveryStages'
] as const;

const props = defineProps({
  preloadedResponse: {
    type: Object as PropType<QueryResponse<ProjectDocumentResponse>>,
    required: true
  },
  path: {
    type: String,
    default: 'new-projects'
  }
});

const {
  public: { apiUrl, gMapsKey }
} = useRuntimeConfig();

const { get } = useHttpMethods();

const localePath = useLocalePath();

const { possibleParamsKeys, createSearchParams, getBreadcrumbs } = useSearch();

const { t, locale } = useI18n();

const { language } = useLanguage();

function tl(key: string, locale: string = 'en') {
  return t(key, null, { locale });
}

const {
  // methods
  show,
  onHide,
  // refs
  visibleRef,
  indexRef,
  imgsRef
} = useEasyLightbox();

const { share } = useShare();

const buttonsAreVisible = ref(true);

const footerIsVisible = ref(false);

const contactFormIsVisible = ref(false);

const shareDialogIsVisible = ref(false);

const SIMILAR_PROPERTIES = 6;

const result = ref<ProjectDocumentResponse | null>();

const title = computed(() => {
  if (!result.value) return '';

  const propertyTypesMap = Object.fromEntries(
    propertySubTypeOptions.map(option => [option.value, t(option.typeText)])
  );

  // Get property type
  let propertyType =
    propertyTypesMap[result.value.subType as keyof typeof propertyTypesMap] ||
    t('seo.defaultSingle');

  propertyType = t('seo.singleSale', [propertyType]);

  // Join location components, filtering out null/empty values
  const location = [
    result.value.projectName,
    result.value.address,
    result.value.city,
    result.value.state || 'Miami'
  ]
    .filter(Boolean)
    .join(', ');

  // Join property type with project name and location
  return t('connectors.in', [propertyType, location]);
});

type TabOption = {
  paramEn?: string;
  paramEs?: string;
  icon: string;
  label: string;
};

const tabOptions = computed<TabOption[]>(() => [
  {
    icon: mdiOfficeBuilding,
    label: t('project.profile')
  },
  {
    icon: mdiMagnify,
    label: t('project.availability'),
    paramEn: tl('paramsAliases.availability', 'en'),
    paramEs: tl('paramsAliases.availability', 'es')
  },
  {
    icon: mdiTimerSand,
    label: t('project.pending'),
    paramEn: tl('paramsAliases.pending', 'en'),
    paramEs: tl('paramsAliases.pending', 'es')
  },
  {
    icon: mdiFileSign,
    label: t('project.closed'),
    paramEn: tl('paramsAliases.closed', 'en'),
    paramEs: tl('paramsAliases.closed', 'es')
  },
  {
    icon: mdiChartBoxOutline,
    label: t('project.stats'),
    paramEn: tl('paramsAliases.statistics', 'en'),
    paramEs: tl('paramsAliases.statistics', 'es')
  }
]);

const tabParam = computed<{
  param: string;
  alternateParam: string;
  index: number;
} | null>(() => {
  // return paramEn or paramEs depending on which is present in the last param of the route

  const param = urlParams.value[urlParams.value.length - 1];

  if (!param) return null;

  const index = tabOptions.value.findIndex(tabOption => {
    return tabOption.paramEn === param || tabOption.paramEs === param;
  });

  if (index === -1) return null;

  const alternateParam =
    tabOptions.value[index].paramEn === param
      ? tabOptions.value[index].paramEs
      : tabOptions.value[index].paramEn;

  return {
    param,
    alternateParam,
    index
  };
});

const selectedTab = ref(0);

watch(selectedTab, () => {
  if (typeof window === 'undefined') {
    return;
  }

  if (selectedTab.value === (tabParam.value?.index || 0)) {
    return;
  }

  const params = [...urlParams.value];

  if (
    tabParam.value?.param === params[params.length - 1] ||
    selectedTab.value === 0
  ) {
    params.pop();
  }

  if (selectedTab.value !== 0) {
    const paramKey = language.value === 'en' ? 'paramEn' : 'paramEs';

    const newTabParam = tabOptions.value[selectedTab.value]?.[paramKey];

    if (newTabParam) {
      params.push(newTabParam);
    }
  }

  window.history.replaceState(
    {},
    document.title,
    localePath(`/${props.path}/${params.join('/')}`)
  );

  urlParams.recompute();

  updateProjectLinkOnStore();
});

const placesLayers = ref<Record<Layer, boolean>>({
  beaches: false,
  malls: false,
  restaurants: false,
  'college-universities': false,
  'public-schools': false,
  'private-schools': false,
  hospitals: false
});

const transactionType = ref<'RES' | 'RNT'>('RES');

const chartTransactionTypeOptions = ref<
  { text: string; value: 'RES' | 'RNT' }[]
>([
  {
    text: t('project.sales'),
    value: 'RES'
  },
  {
    text: t('project.rentals'),
    value: 'RNT'
  }
]);

const isBuilding = computed(
  () => result.value?.projectStatus === 'on-the-market'
);

const completeAddress = computed(() => {
  if (!result.value) return '';

  const addressList = result.value.address ? [result.value.address] : [];

  if (result.value.city && !result.value.address.includes(result.value.city)) {
    addressList.push(result.value.city);
  }

  let postalCode =
    result.value.postalCode !== 'None' ? result.value.postalCode : '';

  if (postalCode && postalCode.includes('.')) {
    postalCode = postalCode.split('.')[0];
  }

  if (postalCode && !result.value.address.includes(postalCode)) {
    addressList.push(`FL ${postalCode}`);
  }

  return addressList.join(', ');
});

const marker = computed(() => {
  if (!result.value?.geopoint) return null;

  return {
    position: {
      lat: result.value.geopoint[0],
      lng: result.value.geopoint[1]
    }
  };
});

const cardProps = computed<ProjectCardPropsExtended | null>(() => {
  if (!result.value || !result.value.geopoint) return null;
  return {
    ...formatProperty(result.value),
    position: result.value?.geopoint
      ? {
          lat: result.value.geopoint[0],
          lng: result.value.geopoint[1]
        }
      : {
          lat: 0,
          lng: 0
        },
    shortPrice: result.value?.minPrice || 0 // not used
  };
});

const summary = ref('');

const splide = ref<{ go: (index: number) => void } | null>(null);

const {
  public: { cloudflareImageProcessing: cfUrl }
} = useRuntimeConfig();

const images = computed<
  {
    image: string;
    imageSet?: string;
    imageAlt: string;
    placeholder?: boolean;
  }[]
>(() => {
  const images =
    (result.value?.photos || []).map((imageUrl: string) => {
      const fixedImageUrl = fixImageUrl(imageUrl);
      const image = `${cfUrl}/image?w=1200&h=505&f=webp&fit=crop&image=${fixedImageUrl}`;
      return {
        image,
        imageSet: `${image} 1x, ${cfUrl}/image?w=2400&h=1010&f=webp&fit=crop&image=${fixedImageUrl} 2x`,
        imageAlt: result.value.address
      };
    }) || [];

  return images?.length
    ? images
    : [
        {
          image: '/images/pfs-placeholder.svg',
          imageAlt: 'PFS Realty Group',
          placeholder: true
        }
      ];
});

const urlParams = useUrlParams();

const searchStore = useSearchStore();

const { selectedLocation: searchLocation } = useSelectableLocation(() => ({
  query: 'projects',
  navigateToLocations: true,
  buyType: 'NEW',
  beforeNavigate: searchStore.saveQueryToStorage
}));

const searchLink = computed(() => {
  const searchParams: string[] = isBuilding.value
    ? searchStore.propertiesSearchParams
    : searchStore.projectSearchParams || [];

  return localePath(`/${props.path}/${searchParams.join('/')}`);
});

function backToSearchEvent() {
  if (typeof window !== 'undefined') {
    window.goingBack = true;
  }
}

const resultIndex = ref<number | null>(null);

const previousId = ref<string | null>(null);
const nextId = ref<string | null>(null);

const similarProjectsResponse =
  ref<QueryResponse<ProjectDocumentResponse> | null>(null);

const similarProjectsRaw = ref<ProjectDocumentResponse[]>([]);

const { getProjectLink, getUsedProjectLink, formatProperty } = useProperties();

const similarProjects = computed<ProjectCardProps[]>(() => {
  return similarProjectsRaw.value.map(formatProperty);
});

// watch(
//   () => [isAuthenticated.value, similarProjects.value],
//   async () => {
//     if (!isAuthenticated.value) return;

//     await updateFavorites(similarProjects.value.map(value => value.id));
//   }
// );

watch(
  () => result.value,
  () => {
    imgsRef.value = result.value?.photos || [];
  }
);

function getBuildingFilters(status: string | null = null) {
  const postalCode = result.value?.postalCode;

  const zipcodes = postalCode
    ? [postalCode, postalCode.split('.')[0], postalCode.split('.')[0] + '.0']
    : null;

  return {
    queryBy: 'full',
    q: result.value?.address || '*',
    zipcode: zipcodes,
    buildingName: result.value?.address ? null : result.value?.projectName,
    status
  };
}

function updateIndex(_oldIndex: number, index: number) {
  indexRef.value = index;
  if (splide.value) {
    splide.value.go(index);
  }
}

async function getSimilarProjects() {
  if (!result.value) return;

  similarProjectsResponse.value = null;
  similarProjectsRaw.value = [];

  const queryParams = {
    collection: 'projects',
    perPage: SIMILAR_PROPERTIES.toString()
  } as Record<string, string | string[]>;

  const filterBy: string[] = [];

  if (result.value.minBeds) {
    filterBy.push(`minBeds:>=${result.value.minBeds}`);
    filterBy.push(`minBeds:<=${result.value.minBeds + 1}`);
  }

  if (result.value.maxBeds) {
    filterBy.push(`maxBeds:>=${result.value.maxBeds}`);
    filterBy.push(`maxBeds:<=${result.value.maxBeds + 1}`);
  }

  if (result.value.minBaths) {
    filterBy.push(`minBaths:>=${result.value.minBaths}`);
    filterBy.push(`minBaths:<=${result.value.minBaths + 2}`);
  }

  if (result.value.maxBaths) {
    filterBy.push(`maxBaths:>=${result.value.maxBaths}`);
    filterBy.push(`maxBaths:<=${result.value.maxBaths + 2}`);
  }

  if (result.value.yearCompletion) {
    filterBy.push(`yearCompletion:>=${result.value.yearCompletion - 2}`);
    filterBy.push(`yearCompletion:<=${result.value.yearCompletion + 2}`);
  }

  if (result.value.minPrice) {
    filterBy.push(`minPrice:>=${result.value.minPrice - 50000}`);
    filterBy.push(`minPrice:<=${result.value.minPrice + 50000}`);
  }

  if (result.value.maxPrice) {
    filterBy.push(`maxPrice:>=${result.value.maxPrice - 50000}`);
    filterBy.push(`maxPrice:<=${result.value.maxPrice + 50000}`);
  }

  if (result.value.id) {
    filterBy.push(`(id:>${result.value.id} || id:<${result.value.id})`);
  }

  if (filterBy.length > 0) {
    queryParams.filterBy = filterBy;
  }

  queryParams.includeFields =
    'address,city,county,id,geopoint,minPrice,photos,projectName,projectStatus,subType,yearCompletion';

  const url =
    apiUrl +
    '/search/projects?' +
    Object.entries(createSearchParams(queryParams))
      .map(vs => new URLSearchParams({ [vs[0]]: vs[1] }))
      .join('&');
  // new URLSearchParams(queryParams as Record<string, string>);

  similarProjectsResponse.value = (
    await get<{ data: QueryResponse<ProjectDocumentResponse> }>(url)
  ).data;

  similarProjectsRaw.value =
    similarProjectsResponse.value?.hits.map(hit => hit.document) || [];
}

function updateBuildingFilters() {
  searchStore.setBuildingFilters(getBuildingFilters());
}

function updateProjectLinkOnStore() {
  const link = !isBuilding.value
    ? `/${props.path}/${getProjectLink(result.value, t)}`
    : `/${props.path}/${getUsedProjectLink(result.value, t)}` +
      (tabParam.value?.param ? `/${tabParam.value?.param}` : '');

  searchStore.setProjectLink(link);
}

async function loadProject() {
  const response = props.preloadedResponse;

  if (!response.hits[0]) {
    console.error('[PROJECT] Response without hits');
    return;
  }

  result.value = response.hits[0].document;
  // result.value = response.hits[0].document;

  const paramsObject = Object.fromEntries(
    urlParams.value
      .map(v => {
        if (v === tabParam.value?.param) {
          return null;
        }

        const key = possibleParamsKeys.find(k => v.endsWith(`-${k}`));
        const underscoreKey = possibleParamsKeys.find(k => v.endsWith(`_${k}`));

        if (key) {
          return [key, v.replace(`-${key}`, '')];
        } else if (underscoreKey) {
          return [underscoreKey, v.replace(`-${underscoreKey}`, '')];
        } else {
          return v.includes('_')
            ? [v.split('_').slice(-1)[0], v.split('_').slice(0, -1).join('_')]
            : [v.split('-').slice(-1)[0], v.split('-').slice(0, -1).join('-')];
        }
      })
      .filter(Boolean)
  );

  resultIndex.value = +paramsObject.result;

  if (resultIndex.value) {
    const hasPrevious = resultIndex.value > 1;

    // -2 since offset starts at 0 and indices start at 1
    const offset = Math.max(resultIndex.value - 2, 0);

    const limit = !hasPrevious ? 1 : 2;

    // TODO: filter current project
    // const projectId = route.params.params[route.params.params.length - 1];

    const queryParams = createSearchParams({
      ...paramsObject,
      collection: 'projects',
      offset,
      limit
      // filterBy: ['id:!=' + projectId]
    });

    delete queryParams.page;

    const previousAndNextResponse = (
      await get<{ data: QueryResponse<ProjectDocumentResponse<'id'>> }>(
        apiUrl +
          '/search/projects?' +
          Object.entries({
            ...queryParams,
            include_fields: 'id'
          })
            .map(vs => new URLSearchParams({ [vs[0]]: vs[1] }))
            .join('&')
      )
    ).data;

    if (previousAndNextResponse.hits.length === 1) {
      if (hasPrevious) {
        previousId.value = previousAndNextResponse.hits[0].document.id;
      } else {
        nextId.value = previousAndNextResponse.hits[0].document.id;
      }
    } else if (previousAndNextResponse.hits.length === 2) {
      previousId.value = previousAndNextResponse.hits[0].document.id;
      nextId.value = previousAndNextResponse.hits[1].document.id;
    }
  }

  // updateFavorites([result.value.id], true);

  updateBuildingFilters();
  updateProjectLinkOnStore();

  await getSimilarProjects();
}

const { addToast } = useToasts();

const copyShareLink = () => {
  navigator.clipboard.writeText(
    `${t('property.shareText')} ${window.location.href}`
  );
  addToast({
    title: t('project.shareLinkCopied'),
    type: 'success'
  });
};

const { data, error } = await useAsyncData(
  'project-component-data',
  async () => {
    if (process.client) {
      return {};
    }
    await loadProject();
    return {
      result: result.value,
      resultIndex: resultIndex.value,
      previousId: previousId.value,
      nextId: nextId.value,
      similarProjectsResponse: similarProjectsResponse.value,
      similarProjectsRaw: similarProjectsRaw.value
    };
  }
);

let link: string = '';

if (error.value || (!data.value?.result && !process.client)) {
  console.error(error.value || '[PROJECT] Project not loaded');
} else if (data.value?.result) {
  result.value = data.value.result;
  resultIndex.value = data.value.resultIndex;
  previousId.value = data.value.previousId;
  nextId.value = data.value.nextId;
  similarProjectsResponse.value = data.value.similarProjectsResponse;
  similarProjectsRaw.value = data.value.similarProjectsRaw;

  link = useCanonicalLink(t =>
    !isBuilding.value
      ? `/${t('routes.projects')}/${getProjectLink(result.value, t)}`
      : `/${t('routes.properties')}/${getUsedProjectLink(result.value, t)}` +
        (tabParam.value?.param ? `/${tabParam.value?.param}` : '')
  ).link;
}

const breadcrumbs = computed(() => {
  if (!result.value) return [];

  // const priceFrom = buyPrices
  //   .filter(price => price.value < result.value.minPrice)
  //   .slice(-1)[0]?.value;

  // const priceTo = buyPrices.filter(
  //   price => price.value > result.value.maxPrice
  // )[0]?.value;

  const query: Record<string, QueryValue> = {
    ...result.value,
    // projectType: result.value.projectStatus,
    propertyType: [propertyTypeMap[result.value.subType]],
    propertySubType: isBuilding.value ? ['building'] : [result.value.subType],
    project: result.value.projectName,
    projectAddress: result.value.address,
    // bedrooms: result.value.maxBeds,
    // bathrooms: result.value.maxBaths,
    // priceFrom,
    // priceTo,
    // yearFrom: result.value.yearCompletion,
    county: result.value.county,
    city: result.value.city,
    zipcode: result.value.postalCode,
    neighborhood: result.value.neighborhood,
    floorPlans: ''
  };

  const breadcrumbs = getBreadcrumbs(
    query,
    isBuilding.value ? tl('routes.properties') : tl('routes.projects')
  );

  const translatedPath = isBuilding.value
    ? t('routes.properties')
    : t('routes.projects');

  if (!breadcrumbs[breadcrumbs.length - 2].item) {
    breadcrumbs[breadcrumbs.length - 2] = {
      ...breadcrumbs[breadcrumbs.length - 2],
      item: !isBuilding.value
        ? localePath(`/${translatedPath}/${getProjectLink(result.value, t)}`)
        : localePath(
            `/${translatedPath}/${getUsedProjectLink(result.value, t)}` +
              (tabParam.value?.param ? `/${tabParam.value?.param}` : '')
          ),
      interactuable: false
    };
  }

  if (!breadcrumbs[breadcrumbs.length - 1].item) {
    breadcrumbs[breadcrumbs.length - 1] = {
      ...breadcrumbs[breadcrumbs.length - 1],
      item: !isBuilding.value
        ? localePath(`/${translatedPath}/${getProjectLink(result.value, t)}`)
        : localePath(
            `/${translatedPath}/${getUsedProjectLink(result.value, t)}` +
              (tabParam.value?.param ? `/${tabParam.value?.param}` : '')
          ),
      interactuable: false
    };
  }

  return breadcrumbs;
});

const host = useHost();

const {
  getProjectSeo,
  getProjectSeoDescription,
  getProjectSchemaName,
  getProjectSchemaDescription
} = useGetSeo();

useSchemaOrg([
  ...useCommonSchemaOrg(link),
  result.value
    ? defineProduct({
        '@id': link + '#product',
        '@type': 'House',
        name: getProjectSchemaName(result.value),
        description: getProjectSchemaDescription(result.value),
        image: images.value[0]?.image,
        url: host + localePath(link),
        telephone: '+19542171791',
        numberOfRooms: result.value.maxBeds,
        numberOfBathroomsTotal: result.value.maxBaths,
        offers: {
          '@type': 'Offer',
          price: result.value.minPrice,
          priceCurrency: 'USD',
          availability: 'InStock'
        },
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'US',
          streetAddress: result.value.address,
          addressLocality: result.value.city,
          addressRegion: result.value.state,
          postalCode: result.value.postalCode
        },
        ...(result.value.geopoint
          ? {
              geo: {
                '@type': 'GeoCoordinates',
                latitude: result.value.geopoint[0],
                longitude: result.value.geopoint[1]
              }
            }
          : {}),
        ...(result.value.maxSqft
          ? {
              floorSize: {
                '@type': 'QuantitativeValue',
                value: result.value.maxSqft,
                unitText: 'SQF'
              }
            }
          : {})
        // amenityFeature: [
        // ]
      })
    : null,
  defineBreadcrumb({
    '@id': link + '#breadcrumb',
    itemListElement: breadcrumbs.value.map(({ name, item }) => ({ name, item }))
  })
]);

useSeoExtended({
  title: () => (result.value ? getProjectSeo(result.value, 'title') : ''),
  description: () =>
    result.value ? getProjectSeoDescription(result.value) : '',
  image: () => images.value[0]?.image || '/images/pfs-placeholder.svg'
});

useHead({
  meta: [
    {
      hid: 'og:locale',
      property: 'og:locale',
      content: t('seo.locale')
    },
    {
      hid: 'og:locale:alternate',
      property: 'og:locale:alternate',
      content: t('seo.alternateLocale')
    },
    {
      hid: 'twitter:card',
      name: 'twitter:card',
      content: 'summary_large_image'
    }
  ]
});

let pathFixed = false;

function fixPath() {
  if (!result.value || pathFixed) {
    return;
  }

  const newPath = localePath(
    !isBuilding.value
      ? `/${props.path}/${getProjectLink(result.value, t)}`
      : `/${props.path}/${getUsedProjectLink(result.value, t)}` +
          (tabParam.value?.param ? `/${tabParam.value?.param}` : '')
  );

  if (newPath !== window.location.pathname) {
    navigateTo(newPath, { replace: true });
  }
  pathFixed = true;
}

const isInMobile = ref(false);

const updateWhatsappLinks = inject('updateWhatsappLinks', (name: string) => {});

onMounted(() => {
  const onResize = () => {
    isInMobile.value = window.innerWidth < 800;
  };

  window.addEventListener('resize', onResize);
  onResize();

  fixPath();

  if (Object.keys(data.value).length === 0) {
    loadProject().then(() => {
      useCanonicalLink(t =>
        !isBuilding.value
          ? `/${t('routes.projects')}/${getProjectLink(result.value, t)}`
          : `/${t('routes.properties')}/${getUsedProjectLink(
              result.value,
              t
            )}` + (tabParam.value?.param ? `/${tabParam.value?.param}` : '')
      );
      updateWhatsappLinks(result.value?.projectName || '');
      updateBuildingFilters();
    });
  } else {
    updateWhatsappLinks(result.value?.projectName || '');
    updateBuildingFilters();
  }

  setTimeout(() => {
    selectedTab.value = tabParam.value?.index || 0;
  }, 300);

  animateDetailsElements();
});
</script>

<style lang="scss">
@import '~/assets/css/splide-theme.scss';

.search-controls-button {
  @apply rounded text-sm h-[36px] md:h-[42px] pl-2 md:pl-2 pr-0.5 md:pr-1;
  // with aira-expanded="true"
  &[aria-expanded='true'],
  &[data-popper-shown],
  .popover-active &,
  &.popover-active {
    .mdi-icon {
      @apply rotate-180;
    }
  }
}

.project-page .hubspotForm {
  iframe {
    min-height: 720px !important;
  }
}

.table-container {
  @apply w-full max-w-[100vw] overflow-x-auto my-[-3px];

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-black/20;
  }

  &::-webkit-scrollbar-thumb:window-inactive {
    @apply bg-black/20;
  }
}

.table-container:hover {
  &::-webkit-scrollbar-thumb {
    @apply bg-black/30;
  }

  &::-webkit-scrollbar-thumb:window-inactive {
    @apply bg-black/30;
  }
}

body:has(.vel-modal) {
  overflow-y: scroll;
  position: fixed;
  width: 100%;
}

.vel-modal {
  @apply bg-black/90 #{!important};
}

.vel-btns-wrapper {
  .btn-next,
  .btn-prev {
    &:disabled {
      opacity: 0.3;
      cursor: default;
    }
  }
}

details {
  summary {
    list-style: none;
    cursor: pointer;
    user-select: none;
    position: relative;
  }

  .arrow {
    @apply transition-transform duration-200;
  }

  &[open] {
    .arrow {
      transform: rotate(-180deg);
    }
  }
}
</style>
